// The identity token, SPEC §4.
//
// A random 128-bit token. `/session` mints one, sets it as a first-party cookie
// server-side, and hands the raw value back so the socket's HELLO can present it.
// The cookie is the durable half: script-writable storage is not permanent —
// WebKit clears it after 7 days without interaction and a private window has none
// — so the browser copy is a cache, and the cookie is what makes a returning
// player the same player.
//
// The raw token is presented exactly once per connection, in HELLO, because the
// browser WebSocket constructor cannot set a request header. Everything after the
// handshake names the short-lived session id instead.

/** What /session answers with. */
export interface SessionResponse {
  token: string;
  recovery_code: string;
  minted: boolean;
  country: number;
  country_locked: boolean;
  banked: number;
  capacity: number;
  tick: number;
}

export interface SessionOptions {
  /** Defaults to the same-origin endpoint the Vite dev server proxies. */
  url?: string;
  fetchImpl?: typeof fetch;
}

/**
 * Fetches the identity token.
 *
 * `credentials: 'include'` is load-bearing: the cookie is the durable identity
 * and a request that does not carry it mints a brand-new player on every visit —
 * a fresh bank of clicks, and no country lock, which is the one thing §4's lock
 * exists to prevent.
 */
export async function fetchSession(opts: SessionOptions = {}): Promise<SessionResponse> {
  const f = opts.fetchImpl ?? fetch;
  const res = await f(opts.url ?? '/session', {
    credentials: 'include',
    headers: { accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`net: /session answered ${res.status}`);
  return (await res.json()) as SessionResponse;
}

/** The token alone, in the shape Connection's `token` option wants. */
export function sessionToken(opts: SessionOptions = {}): () => Promise<string> {
  return async () => (await fetchSession(opts)).token;
}

/**
 * The socket URL for the current page. The game server owns both the socket and
 * the HTTP endpoints, so the socket is same-origin and the dev server proxies it.
 */
export function defaultSocketURL(loc: { protocol: string; host: string } = location): string {
  const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${loc.host}/ws`;
}
